import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import type { ReactNode, CSSProperties } from 'react'
import { useOrgBrandingStore } from '@/stores/orgBrandingStore'
import { getThemeCssVariables } from '../../../shared/organizationTheme'

// ── 凡例 ─────────────────────────────────────────────────────
// このファイルは 2 つの独立した confirm 系統を提供する:
//   1. useConfirm / useAlert / ConfirmProvider / ConfirmModal     ── 既存。汎用確認ダイアログ
//   2. useConfirmWithAcknowledge / AcknowledgeConfirmProvider /   ── 巻戻し用。チェックボックス必須
//      AcknowledgeConfirmModal
// 系統 2 は系統 1 と完全に独立しており、互いに副作用なし。

/**
 * アプリ全体で共通の確認ダイアログ。
 * ネイティブ `window.confirm()` の代替として、画面上に
 * 統一デザインのモーダルを表示する。
 *
 * 使い方:
 *   const confirm = useConfirm()
 *   const ok = await confirm({
 *     title:        'イベントを中断しますか？',
 *     message:      '進行中の対戦はすべて失われます。',
 *     confirmLabel: '中断する',
 *     danger:       true,
 *   })
 *   if (!ok) return
 *
 * モバイルでは縦積み全幅、sm 以上で右寄せ横並びに切り替わる。
 */

export type ConfirmOptions = {
  title?:        string
  message:       string | ReactNode
  confirmLabel?: string
  cancelLabel?:  string
  danger?:       boolean
  alertOnly?:    boolean  // true: OK ボタンのみ表示（アラート用途）
}

type Resolver = (ok: boolean) => void

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext)
  if (!fn) throw new Error('useConfirm must be used inside <ConfirmProvider>')
  return fn
}

/**
 * OK ボタンのみのアラートダイアログ。OS の `window.alert()` の代替。
 *
 *   const alert = useAlert()
 *   await alert({ title: 'エラー', message: '操作に失敗しました' })
 */
export function useAlert() {
  const confirm = useConfirm()
  return useCallback(
    (opts: { title?: string; message: string | ReactNode; okLabel?: string }) => {
      return confirm({
        title:        opts.title,
        message:      opts.message,
        confirmLabel: opts.okLabel ?? 'OK',
        alertOnly:    true,
      })
    },
    [confirm],
  )
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ opts: ConfirmOptions; resolve: Resolver } | null>(null)

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setState({ opts, resolve })
    })
  }, [])

  const close = useCallback((ok: boolean) => {
    setState((prev) => {
      if (prev) prev.resolve(ok)
      return null
    })
  }, [])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <ConfirmModal
          opts={state.opts}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      )}
    </ConfirmContext.Provider>
  )
}

// ── 内部コンポーネント ──────────────────────────────────────────

function ConfirmModal({
  opts,
  onConfirm,
  onCancel,
}: {
  opts:      ConfirmOptions
  onConfirm: () => void
  onCancel:  () => void
}) {
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null)
  const alertOnlyMode = opts.alertOnly ?? false
  // モーダルは App ルート（テーマ適用スコープの外）で描画されるため、
  // 主催者団体のテーマ変数をモーダル自身に当てて bg-primary を追従させる。
  // 非主催者・未取得時は branding=null → TAIARI 標準（赤）でフォールバック。
  const branding = useOrgBrandingStore((s) => s.branding)

  // ESC でキャンセル（alertOnly のときは OK 扱い）
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (alertOnlyMode) onConfirm()
        else onCancel()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onCancel, onConfirm, alertOnlyMode])

  // オープン時に確定ボタンへフォーカス
  useEffect(() => {
    confirmBtnRef.current?.focus()
  }, [])

  const alertOnly    = opts.alertOnly ?? false
  const title        = opts.title ?? (alertOnly ? 'お知らせ' : '確認')
  const confirmLabel = opts.confirmLabel ?? 'OK'
  const cancelLabel  = opts.cancelLabel ?? 'キャンセル'
  const danger       = opts.danger ?? false

  // alertOnly の場合は確定ボタンは Primary 色固定（破壊的アクションではないため）
  const confirmClass = alertOnly
    ? 'bg-primary hover:bg-primary/90 text-white'
    : danger
      ? 'bg-red-600 hover:bg-red-700 text-white'
      : 'bg-primary hover:bg-primary/90 text-white'

  // alertOnly のときは ESC / 背景クリックで dismiss は OK 扱い（confirm 経路）にして
  // 「キャンセルを返す」状態を生まないようにする
  const dismiss = alertOnly ? onConfirm : onCancel

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      style={getThemeCssVariables(branding) as CSSProperties}
      onPointerDown={dismiss}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        className="bg-white rounded-lg shadow-lg w-[440px] max-w-full flex flex-col"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-neutral-200">
          <p id="confirm-dialog-title" className="text-sm font-semibold text-neutral-800">
            {title}
          </p>
        </div>
        <div className="px-4 py-3 text-sm text-neutral-700 whitespace-pre-wrap break-words">
          {opts.message}
        </div>
        <div className="px-4 py-3 border-t border-neutral-200 flex flex-col-reverse gap-1.5 sm:flex-row sm:justify-end sm:gap-2">
          {!alertOnly && (
            <button
              type="button"
              onClick={onCancel}
              className="w-full sm:w-auto px-4 py-2 rounded border border-neutral-300 text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50 whitespace-nowrap"
            >
              {cancelLabel}
            </button>
          )}
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={onConfirm}
            className={`w-full sm:w-auto px-4 py-2 rounded text-sm font-medium whitespace-nowrap ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// 系統 2: チェックボックス必須の confirm（巻戻し用）
// 既存系統 1 とは完全独立。Context・Provider・Modal すべて別実装。
// ============================================================

/**
 * チェックボックス必須の確認ダイアログ用フック。
 *
 *   const confirm = useConfirmWithAcknowledge()
 *   const ok = await confirm({
 *     title:             '巻戻しの確認',
 *     message:           <>「進行中」→「当日受付中」 に戻します</>,
 *     warnings:          ['対戦カード 12件 を削除', '参加者 24名 のステータスをリセット'],
 *     acknowledgeLabel:  '上記の影響を理解し、巻戻しを実行します',
 *     confirmLabel:      '巻戻す',
 *   })
 *   if (!ok) return
 */
export type AcknowledgeConfirmOptions = {
  title?:           string
  message:          string | ReactNode
  warnings?:        string[]              // 赤系の影響件数リスト（任意）
  acknowledgeLabel: string                // チェックボックスのラベル
  confirmLabel?:    string                // 実行ボタンのラベル（既定: 'OK'）
  cancelLabel?:    string                 // キャンセルボタンのラベル（既定: 'キャンセル'）
  danger?:          boolean               // 実行ボタンを red 系にする（既定: amber 系）
}

type AcknowledgeResolver = (ok: boolean) => void
type AcknowledgeConfirmFn = (opts: AcknowledgeConfirmOptions) => Promise<boolean>

const AcknowledgeConfirmContext = createContext<AcknowledgeConfirmFn | null>(null)

export function useConfirmWithAcknowledge(): AcknowledgeConfirmFn {
  const fn = useContext(AcknowledgeConfirmContext)
  if (!fn) throw new Error('useConfirmWithAcknowledge must be used inside <AcknowledgeConfirmProvider>')
  return fn
}

export function AcknowledgeConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ opts: AcknowledgeConfirmOptions; resolve: AcknowledgeResolver } | null>(null)

  const confirm = useCallback<AcknowledgeConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setState({ opts, resolve })
    })
  }, [])

  const close = useCallback((ok: boolean) => {
    setState((prev) => {
      if (prev) prev.resolve(ok)
      return null
    })
  }, [])

  return (
    <AcknowledgeConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <AcknowledgeConfirmModal
          opts={state.opts}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      )}
    </AcknowledgeConfirmContext.Provider>
  )
}

function AcknowledgeConfirmModal({
  opts,
  onConfirm,
  onCancel,
}: {
  opts:      AcknowledgeConfirmOptions
  onConfirm: () => void
  onCancel:  () => void
}) {
  const [acknowledged, setAcknowledged] = useState(false)
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null)

  // ESC でキャンセル
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCancel()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onCancel])

  // オープン時はキャンセルボタンにフォーカス（事故防止）
  useEffect(() => {
    cancelBtnRef.current?.focus()
  }, [])

  const title        = opts.title ?? '確認'
  const confirmLabel = opts.confirmLabel ?? 'OK'
  const cancelLabel  = opts.cancelLabel ?? 'キャンセル'
  const danger       = opts.danger ?? false

  const confirmClass = danger
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-amber-600 hover:bg-amber-700 text-white'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onPointerDown={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ack-confirm-dialog-title"
    >
      <div
        className="bg-white rounded-lg shadow-lg w-[460px] max-w-full flex flex-col"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-neutral-200">
          <p id="ack-confirm-dialog-title" className="text-sm font-semibold text-neutral-800">
            {title}
          </p>
        </div>
        <div className="px-4 py-3 text-sm text-neutral-700 whitespace-pre-wrap break-words">
          {opts.message}
          {opts.warnings && opts.warnings.length > 0 && (
            <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded">
              <p className="text-xs font-semibold text-amber-800 mb-1">⚠️ 以下の影響をご確認ください</p>
              {/* list-outside + pl-3: 長い項目が折り返しても2行目以降がテキスト左端に揃う */}
              <ul className="text-xs text-amber-900 list-disc list-outside pl-3 space-y-0.5">
                {opts.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          <label className="mt-3 flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 shrink-0"
            />
            <span className="text-sm text-neutral-700">{opts.acknowledgeLabel}</span>
          </label>
        </div>
        <div className="px-4 py-3 border-t border-neutral-200 flex flex-col-reverse gap-1.5 sm:flex-row sm:justify-end sm:gap-2">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onCancel}
            className="w-full sm:w-auto px-4 py-2 rounded border border-neutral-300 text-sm font-medium text-neutral-700 bg-white hover:bg-neutral-50 whitespace-nowrap"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!acknowledged}
            className={`w-full sm:w-auto px-4 py-2 rounded text-sm font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
